import { useState, useEffect, useCallback } from 'react';
import { Search, MapPin, Calendar, Heart, Filter, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { apiUrl } from '../utils/api';

interface CampaignSummary {
  id: string;
  title: string;
  description: string;
  deadline: string;
  location: {
    country: string;
    state: string;
    city: string;
    address: string;
  };
  progressPercentage: number;
  urgencyScore: number;
  publicLink: string;
  isVerified: boolean;
}

export function MarketplacePage() {
  const [campaigns, setCampaigns] = useState<CampaignSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [locationFilter, setLocationFilter] = useState({ country: '', state: '', city: '' });
  const [showFilters, setShowFilters] = useState(false);
  const [totalResults, setTotalResults] = useState(0);

  const loadCampaigns = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      
      if (searchQuery.trim()) {
        params.set('q', searchQuery.trim());
      }
      if (locationFilter.country) params.set('country', locationFilter.country);
      if (locationFilter.state) params.set('state', locationFilter.state);
      if (locationFilter.city) params.set('city', locationFilter.city);
      params.set('limit', '20');

      const endpoint = searchQuery.trim() || locationFilter.country || locationFilter.state || locationFilter.city
        ? `/api/campaigns/search?${params.toString()}`
        : `/api/campaigns/public?${params.toString()}`;

      const response = await fetch(apiUrl(endpoint));
      const data = await response.json();
      
      if (data.success) {
        setCampaigns(data.data || []);
        setTotalResults(data.data?.length || 0);
      }
    } catch (error) {
      console.error('Error loading campaigns:', error);
    } finally {
      setIsLoading(false);
    }
  }, [searchQuery, locationFilter]);

  useEffect(() => {
    loadCampaigns();
  }, [loadCampaigns]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    loadCampaigns();
  };

  const clearFilters = () => {
    setSearchQuery('');
    setLocationFilter({ country: '', state: '', city: '' });
  };

  const hasActiveFilters = searchQuery || locationFilter.country || locationFilter.state || locationFilter.city;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const isDeadlineSoon = (deadline: string) => {
    const deadlineDate = new Date(deadline);
    const today = new Date();
    deadlineDate.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);
    const diffDays = Math.round((deadlineDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    return diffDays <= 7 && diffDays > 0;
  };

  const isExpired = (deadline: string) => {
    return new Date(deadline) < new Date();
  };

  const getUrgencyLabel = (score: number) => {
    if (score >= 3) return { text: 'Urgente', color: 'bg-red-100 text-red-800' };
    if (score >= 2) return { text: 'Próxima', color: 'bg-yellow-100 text-yellow-800' };
    return { text: 'Activa', color: 'bg-green-100 text-green-800' };
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">
              Marketplace de Donaciones
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-8">
              Descubre campañas verificadas y ayuda a quienes más lo necesitan.
            </p>
            
            {/* Search Bar */}
            <form onSubmit={handleSearch} className="max-w-2xl mx-auto">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="block w-full pl-10 pr-24 py-3 border border-gray-300 rounded-lg bg-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Buscar campañas por nombre o descripción..."
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-2">
                  <button
                    type="button"
                    onClick={() => setShowFilters(!showFilters)}
                    className={`p-2 rounded-md transition-colors ${showFilters ? 'bg-primary-100 text-primary-600' : 'text-gray-400 hover:text-gray-600'}`}
                    aria-label="Filtros"
                  >
                    <Filter className="h-5 w-5" />
                  </button>
                  <button
                    type="submit"
                    className="ml-1 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-md hover:bg-primary-700"
                  >
                    Buscar
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters Panel */}
        {showFilters && (
          <div className="bg-white rounded-lg shadow p-6 mb-8">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Filtros de ubicación</h3>
              {hasActiveFilters && (
                <button onClick={clearFilters} className="text-sm text-primary-600 hover:text-primary-800 flex items-center">
                  <X className="h-4 w-4 mr-1" /> Limpiar filtros
                </button>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">País</label>
                <input
                  type="text"
                  value={locationFilter.country}
                  onChange={(e) => setLocationFilter(prev => ({ ...prev, country: e.target.value }))}
                  placeholder="Ej: México"
                  className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 px-3 py-2 border"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Estado</label>
                <input
                  type="text"
                  value={locationFilter.state}
                  onChange={(e) => setLocationFilter(prev => ({ ...prev, state: e.target.value }))}
                  placeholder="Ej: Jalisco"
                  className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 px-3 py-2 border"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Ciudad</label>
                <input
                  type="text"
                  value={locationFilter.city}
                  onChange={(e) => setLocationFilter(prev => ({ ...prev, city: e.target.value }))}
                  placeholder="Ej: Guadalajara"
                  className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 px-3 py-2 border"
                />
              </div>
            </div>
          </div>
        )}

        {/* Results info */}
        {!isLoading && (
          <div className="mb-4 text-sm text-gray-600">
            {totalResults} campaña{totalResults !== 1 ? 's' : ''} encontrada{totalResults !== 1 ? 's' : ''}
            {hasActiveFilters && <span className="text-primary-600 ml-1">(con filtros activos)</span>}
          </div>
        )}

        {/* Content */}
        {isLoading ? (
          <div className="bg-white rounded-lg shadow">
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">Cargando campañas...</p>
            </div>
          </div>
        ) : campaigns.length === 0 ? (
          <div className="bg-white rounded-lg shadow">
            <div className="text-center py-12">
              <Heart className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">
                {hasActiveFilters ? 'No se encontraron resultados' : 'No hay campañas disponibles'}
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                {hasActiveFilters 
                  ? 'Intenta ajustar los filtros de búsqueda.' 
                  : 'Aún no hay campañas públicas verificadas en el marketplace.'}
              </p>
              {hasActiveFilters && (
                <button onClick={clearFilters} className="mt-4 btn btn-outline">
                  Limpiar filtros
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {campaigns.map((campaign) => {
              const urgency = getUrgencyLabel(campaign.urgencyScore);
              return (
                <div key={campaign.id} className="bg-white rounded-lg shadow overflow-hidden hover:shadow-lg transition-shadow">
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-3">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${urgency.color}`}>
                        {urgency.text}
                      </span>
                      {campaign.isVerified && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          ✓ Verificada
                        </span>
                      )}
                    </div>
                    
                    <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2">
                      {campaign.title}
                    </h3>
                    
                    <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                      {campaign.description}
                    </p>

                    {/* Progress bar */}
                    <div className="mb-4">
                      <div className="flex justify-between text-xs text-gray-500 mb-1">
                        <span>Progreso</span>
                        <span>{Math.round(campaign.progressPercentage)}%</span>
                      </div>
                      <div className="w-full bg-gray-100 rounded-full h-2">
                        <div 
                          className="h-2 rounded-full bg-primary-600 transition-all"
                          style={{ width: `${Math.min(campaign.progressPercentage, 100)}%` }}
                        />
                      </div>
                    </div>
                    
                    <div className="flex items-center text-sm text-gray-500 mb-4">
                      <MapPin className="h-4 w-4 mr-1 flex-shrink-0" />
                      <span className="truncate">{campaign.location.city}, {campaign.location.state}</span>
                    </div>

                    <div className="flex items-center text-sm text-gray-500 mb-4">
                      <Calendar className="h-4 w-4 mr-1 flex-shrink-0" />
                      <span>
                        {isExpired(campaign.deadline) 
                          ? 'Expirada' 
                          : `Vence: ${formatDate(campaign.deadline)}`}
                      </span>
                    </div>
                    
                    <Link 
                      to={`/campaigns/${campaign.id}`}
                      className="block w-full text-center px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-md hover:bg-primary-700 transition-colors"
                    >
                      Ver Detalles y Donar
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
